import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { cn } from '../../../utils/cn';

const VoiceSampleCard = ({ voice, duration, isSelected, isPlaying, isPaused, onSelect, onPlay }) => {
  const { t } = useTranslation();
  const language = voice?.languageKey
    ? t(`ai.voices.languages.${voice.languageKey}`)
    : voice?.language;
  const gender = voice?.genderKey
    ? t(`ai.voices.genders.${voice.genderKey}`)
    : voice?.gender;
  const meta = [language, gender].filter(Boolean).join(' • ');

  const handlePlay = () => {
    onPlay?.();
  };

  return (
    <div className="flex min-w-0 flex-col gap-2">
    <div
      className={cn(
        'relative min-w-0 border-2 rounded-xl p-4 transition-all duration-250 cursor-pointer',
        isSelected
          ? 'bg-blue-100 border-primary shadow-elevation-md hover:bg-blue-200/80'
          : 'bg-card border-border hover:shadow-elevation-md',
      )}
      onClick={onSelect}
      role="button"
      tabIndex={0}
      aria-label={t('ai.voices.selectAria', { name: voice?.name, language })}
      onKeyDown={(e) => {
        if (e?.key === 'Enter' || e?.key === ' ') {
          e?.preventDefault();
          onSelect();
        }
      }}
    >
      {isSelected && (
        <div className="absolute top-3 right-3 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
          <Icon name="Check" size={14} color="#FFFFFF" />
        </div>
      )}
      <div className="flex items-center gap-2.5 py-1">
        <div
          className={cn(
            'w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0',
            isSelected ? 'bg-gradient-to-br from-primary to-accent' : 'bg-muted',
          )}
        >
          <Icon
            name="Mic2"
            size={20}
            color={isSelected ? '#FFFFFF' : 'var(--color-muted-foreground)'}
          />
        </div>
        <div className="flex-1 min-w-0 pr-5">
          <h3 className="text-base font-semibold text-foreground mb-1.5 truncate">{voice?.name}</h3>
          {meta && (
            <span
              className={cn(
                'inline-flex max-w-full items-center px-2 py-0.5 text-[11px] leading-snug font-medium rounded-full border',
                isSelected
                  ? 'bg-primary border-primary text-primary-foreground'
                  : 'bg-blue-100 border-primary text-primary',
              )}
            >
              {meta}
            </span>
          )}
        </div>
      </div>
    </div>
      {voice?.sampleUrl && onPlay && (
        <div
          className="flex flex-col gap-1.5"
          onClick={(e) => e.stopPropagation()}
          role="presentation"
        >
        <Button
          variant="outline"
          size="xs"
          iconName={isPlaying ? 'Pause' : 'Play'}
          iconPosition="left"
          onClick={handlePlay}
          className={cn('h-10 w-full', isSelected && 'border-2 border-primary')}
        >
          <span className="flex w-full items-center justify-between gap-1">
            <span className="truncate">
              {isPlaying ? t('ai.voices.pause') : isPaused ? t('ai.voices.resume') : t('ai.voices.playSample')}
            </span>
            <span className="inline-flex shrink-0 items-center gap-1 text-[11px]">
              <Icon name="Clock" size={12} />
              {duration ?? t('common.emDash')}
            </span>
          </span>
        </Button>
        </div>
      )}
    </div>
  );
};

export default VoiceSampleCard;
